/**
 * ForgotPassword — /forgot-password
 *
 * Design from the Claude Design auth handoff. User enters their email,
 * backend sends a reset link, and we always show the "check your inbox"
 * state (the backend never reveals whether the email exists).
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authApi } from '../services/api'
import AuthLayout from '../components/AuthLayout'
import {
  Field, PrimaryButton, ErrorBanner, FormHeader, TextLink, SuccessBadge,
  validators, MailIcon, ArrowLeft,
} from '../components/AuthFields'
import { JAI_COLORS, JAI_FONTS } from '../components/JaiBrand'

export default function ForgotPassword() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [touched, setTouched] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [formErr, setFormErr] = useState('')

  const eErr = touched ? validators.email(email) : ''

  const handleSubmit = async (e) => {
    e.preventDefault()
    setTouched(true)
    if (validators.email(email)) return
    setFormErr('')
    setLoading(true)
    try {
      await authApi.requestPasswordReset(email)
      setSent(true)
    } catch {
      setFormErr('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout screen="forgot">
      {sent ? (
        <div>
          <SuccessBadge icon={<MailIcon size={26} stroke={JAI_COLORS.blue} sw={1.8} />} />
          <FormHeader title="Check your inbox" />
          <p style={{ fontSize: 13.5, color: JAI_COLORS.mute, margin: '-14px 0 0', fontFamily: JAI_FONTS.tight, lineHeight: 1.6 }}>
            If an account exists for <b style={{ color: JAI_COLORS.navy }}>{email}</b>, we've sent a link to reset your password. It expires in 1 hour.
          </p>
          <div style={{ marginTop: 22 }}>
            <PrimaryButton type="button" onClick={() => navigate('/login')}>Back to sign in</PrimaryButton>
          </div>
          <p style={{ textAlign: 'center', fontSize: 12.5, color: JAI_COLORS.mute, marginTop: 16, fontFamily: JAI_FONTS.tight }}>
            Didn't get it? <TextLink size={12.5} onClick={() => setSent(false)}>Try another email</TextLink>
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <button type="button" onClick={() => navigate('/login')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: JAI_COLORS.mute, fontSize: 12.5, fontFamily: JAI_FONTS.tight, padding: 0, marginBottom: 14 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = JAI_COLORS.navy)} onMouseLeave={(e) => (e.currentTarget.style.color = JAI_COLORS.mute)}>
            <ArrowLeft size={15} /> Back to sign in
          </button>
          <FormHeader title="Reset your password" sub="Enter your account email and we'll send you a secure link to set a new password." />
          <ErrorBanner>{formErr}</ErrorBanner>
          <Field label="Email address" type="email" value={email} autoFocus autoComplete="email"
            onChange={setEmail} onBlur={() => setTouched(true)} placeholder="you@example.com"
            error={eErr} valid={!validators.email(email)} leftIcon={<MailIcon size={16} />} />
          <div style={{ marginTop: 12 }}>
            <PrimaryButton loading={loading}>{loading ? 'Sending link…' : 'Send reset link'}</PrimaryButton>
          </div>
        </form>
      )}
    </AuthLayout>
  )
}
